/**
 * V1 Rule Pack Editor — supports email notifications and form assignments
 * for client intake and Episode of Care workflows.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Container,
  Divider,
  Group,
  List,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconCheck,
  IconCloudUpload,
  IconDeviceFloppy,
  IconEye,
  IconGitBranch,
  IconSettings,
} from '@tabler/icons-react';

import {
  ACTION_TYPES,
  CONFLICT_POLICIES,
  RECIPIENT_POLICIES,
  RULE_ACTION_KEYS,
  RULE_PACK_DOMAINS,
  TRIGGER_EVENT_KINDS,
  TRIGGER_RESOURCE_TYPES,
  type ConflictPolicy,
  type RulePackDraft,
} from '../types';
import { validateRulePackDraft } from '../utils/rulesEngineValidation';

import { RulePackStatusBadge } from './RulePackStatusBadge';
import { WorkflowCanvas } from './workflow/WorkflowCanvas';
import { NodeEditDrawer, type DrawerTarget } from './workflow/NodeEditDrawer';

interface Props {
  initial: RulePackDraft;
  onSaveDraft: (draft: RulePackDraft) => Promise<RulePackDraft>;
  onPublish: (draft: RulePackDraft) => Promise<RulePackDraft>;
  onBack: () => void;
  saving: boolean;
  publishing: boolean;
}

export function RulePackEditor({
  initial,
  onSaveDraft,
  onPublish,
  onBack,
  saving,
  publishing,
}: Props): JSX.Element {
  const [draft, setDraft] = useState<RulePackDraft>(initial);
  const [dirty, setDirty] = useState(false);
  const [drawerTarget, setDrawerTarget] = useState<DrawerTarget>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const canvasWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const validation = useMemo(() => validateRulePackDraft(draft), [draft]);

  const update = useCallback(
    (partial: Partial<RulePackDraft>) => {
      setDraft((prev) => ({ ...prev, ...partial }));
      setDirty(true);
    },
    [],
  );

  const handleSave = async () => {
    try {
      const savedDraft = await onSaveDraft(draft);
      setDraft((prev) => ({ ...prev, id: savedDraft.id, version: savedDraft.version, stableRuleKey: savedDraft.stableRuleKey }));
      setDirty(false);
      notifications.show({ title: 'Draft saved', message: 'Rule draft saved.', color: 'green' });
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'Could not save draft.',
        color: 'red',
      });
    }
  };

  const handlePublish = async () => {
    if (!validation.valid) {
      notifications.show({
        title: 'Validation failed',
        message: `${validation.errors.length} issue(s) must be fixed before publishing.`,
        color: 'red',
      });
      return;
    }
    try {
      const publishedDraft = await onPublish(draft);
      setDraft((prev) => ({
        ...prev,
        id: publishedDraft.id,
        version: publishedDraft.version,
        status: publishedDraft.status,
        stableRuleKey: publishedDraft.stableRuleKey,
      }));
      setDirty(false);
      notifications.show({ title: 'Published', message: 'Rule is now active.', color: 'green' });
    } catch (err) {
      notifications.show({
        title: 'Publish failed',
        message: err instanceof Error ? err.message : 'Could not publish.',
        color: 'red',
      });
    }
  };

  const isNew = !draft.id;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Group gap="sm">
            <Button
              variant="subtle"
              color="gray"
              size="sm"
              leftSection={<IconArrowLeft size={16} />}
              onClick={onBack}
            >
              Back
            </Button>
            <div>
              <Group gap="sm">
                <Title order={2}>{draft.title || 'New rule'}</Title>
                <RulePackStatusBadge status={draft.status} />
              </Group>
              <Text size="sm" c="dimmed" mt={2}>
                {isNew ? 'Create a new rule' : `Version ${draft.version ?? '—'}`}
                {dirty && ' · Unsaved changes'}
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button
              variant="default"
              leftSection={<IconDeviceFloppy size={16} />}
              loading={saving}
              disabled={!dirty}
              onClick={handleSave}
            >
              Save draft
            </Button>
            <Button
              color="gold"
              leftSection={<IconCloudUpload size={16} />}
              loading={publishing}
              disabled={!validation.valid}
              onClick={handlePublish}
            >
              Publish
            </Button>
          </Group>
        </Group>

        <Tabs defaultValue="workflow" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="workflow" leftSection={<IconGitBranch size={16} />}>
              Editor
            </Tabs.Tab>
            <Tabs.Tab value="definition" leftSection={<IconSettings size={16} />}>
              Settings
            </Tabs.Tab>
            <Tabs.Tab value="preview" leftSection={<IconEye size={16} />}>
              Review
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="workflow" pt="md">
            <div
              ref={canvasWrapRef}
              style={fullscreen ? {
                position: 'fixed',
                inset: 0,
                zIndex: 200,
                background: 'var(--mantine-color-body)',
                overflow: 'auto',
              } : undefined}
            >
              <Group justify="flex-end" mb="xs" px={fullscreen ? 'md' : 0} pt={fullscreen ? 'xs' : 0}>
                <Tooltip label={fullscreen ? 'Exit full screen' : 'Full screen'} position="left">
                  <ActionIcon
                    variant="default"
                    size="md"
                    onClick={() => setFullscreen((f) => !f)}
                    aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
                  >
                    {fullscreen ? <IconArrowsMinimize size={16} /> : <IconArrowsMaximize size={16} />}
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Paper withBorder={!fullscreen} radius={fullscreen ? 0 : 'md'} style={{ overflow: 'auto' }}>
                <WorkflowCanvas
                  draft={draft}
                  onUpdate={update}
                  onEditTrigger={() => setDrawerTarget({ kind: 'trigger' })}
                  onEditBranch={(branchId) =>
                    setDrawerTarget({ kind: 'branch', branchId })
                  }
                  onEditAction={(actionId) =>
                    setDrawerTarget({ kind: 'action', actionId })
                  }
                />
              </Paper>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="definition" pt="md">
            <Stack gap="md">
              <Paper withBorder p="lg" radius="md">
                <Stack gap="md">
                  <TextInput
                    label="Rule name"
                    placeholder="e.g. After Intake Completion"
                    value={draft.title}
                    onChange={(e) => update({ title: e.currentTarget.value })}
                    withAsterisk
                  />
                  <Textarea
                    label="Description"
                    placeholder="Describe what this rule does"
                    value={draft.description}
                    onChange={(e) => update({ description: e.currentTarget.value })}
                    minRows={3}
                  />
                  <Group grow>
                    <TextInput
                      label="Category"
                      value={RULE_PACK_DOMAINS.find((d) => d.value === draft.domain)?.label ?? draft.domain}
                      readOnly
                    />
                    <Select
                      label="Conflict policy"
                      data={CONFLICT_POLICIES.map((c) => ({ value: c.value, label: c.label }))}
                      value={draft.conflictPolicy}
                      onChange={(v) =>
                        update({ conflictPolicy: (v as ConflictPolicy) ?? draft.conflictPolicy })
                      }
                    />
                  </Group>
                </Stack>
              </Paper>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="preview" pt="md">
            <Stack gap="md">
              <Paper withBorder p="lg" radius="md">
                <Stack gap="sm">
                  <Group gap="sm">
                    {validation.valid ? (
                      <IconCheck size={18} color="var(--mantine-color-green-6)" />
                    ) : (
                      <IconAlertCircle size={18} color="var(--mantine-color-red-6)" />
                    )}
                    <Text fw={600} size="sm">
                      {validation.valid ? 'Ready to publish' : `${validation.errors.length} validation issue(s)`}
                    </Text>
                  </Group>
                  {!validation.valid && (
                    <List size="sm" spacing={4}>
                      {validation.errors.map((err, i) => (
                        <List.Item key={i}>
                          <Text size="sm" c="red.7">
                            <Text span fw={500}>{err.path}:</Text> {err.message}
                          </Text>
                        </List.Item>
                      ))}
                    </List>
                  )}
                </Stack>
              </Paper>

              <Paper withBorder p="lg" radius="md">
                <Stack gap="sm">
                  <Text fw={600} size="sm">Rule summary</Text>
                  <Divider />
                  <SummaryRow label="Trigger resources" value={
                    draft.trigger.resourceTypes.length > 0
                      ? draft.trigger.resourceTypes.map((rt) => TRIGGER_RESOURCE_TYPES.find((t) => t.value === rt)?.label ?? rt).join(', ')
                      : '(none configured)'
                  } />
                  <SummaryRow label="Trigger events" value={
                    draft.trigger.eventKinds.length > 0
                      ? draft.trigger.eventKinds.map((ek) => TRIGGER_EVENT_KINDS.find((e) => e.value === ek)?.label ?? ek).join(', ')
                      : '(none configured)'
                  } />
                  <SummaryRow label="Branches" value={`${draft.rungs.length} (${draft.rungs.filter((r) => r.enabled).length} enabled)`} />
                  <Divider />
                  {draft.rungs.filter((r) => r.enabled).map((rung) => (
                    <Stack key={rung.id} gap={4}>
                      <Text size="sm" fw={500}>
                        {rung.name || 'Unnamed branch'}
                        {rung.alwaysRun && ' (Always run)'}
                      </Text>
                      {rung.actions.map((action) => (
                        <Stack key={action.id} gap={2} pl="md">
                          <SummaryRow label="Action type" value={ACTION_TYPES.find((t) => t.value === action.type)?.label ?? action.type} />
                          {action.actionKey && <SummaryRow label="Action key" value={RULE_ACTION_KEYS.find((k) => k.value === action.actionKey)?.label ?? action.actionKey} />}
                          {action.formTemplateRefs && action.formTemplateRefs.length > 0 && (
                            <SummaryRow label="Forms" value={action.formTemplateRefs.map((f) => f.title).join(', ')} />
                          )}
                          {action.notificationRecipientPolicies && action.notificationRecipientPolicies.length > 0 && (
                            <SummaryRow label="Notification recipients" value={
                              action.notificationRecipientPolicies.map((rp) => RECIPIENT_POLICIES.find((r) => r.value === rp)?.label ?? rp).join(', ')
                            } />
                          )}
                          {action.emailRecipientPolicies && action.emailRecipientPolicies.length > 0 && (
                            <SummaryRow label="Email recipients" value={
                              action.emailRecipientPolicies.map((rp) => RECIPIENT_POLICIES.find((r) => r.value === rp)?.label ?? rp).join(', ')
                            } />
                          )}
                          {action.emailTemplateRef?.title && (
                            <SummaryRow label="Email template" value={action.emailTemplateRef.title} />
                          )}
                          {action.assignmentRecipientPolicies && action.assignmentRecipientPolicies.length > 0 && (
                            <SummaryRow label="Assignment recipients" value={
                              action.assignmentRecipientPolicies.map((rp) => RECIPIENT_POLICIES.find((r) => r.value === rp)?.label ?? rp).join(', ')
                            } />
                          )}
                          {action.timingBucket && <SummaryRow label="Timing" value={action.timingBucket} />}
                        </Stack>
                      ))}
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              {!validation.valid && (
                <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                  Fix the validation issues above before publishing this rule.
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <NodeEditDrawer
        target={drawerTarget}
        draft={draft}
        onUpdate={(partial) => {
          update(partial);
        }}
        onClose={() => setDrawerTarget(null)}
      />
    </Container>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Group gap="xs">
      <Text size="xs" c="dimmed" w={160}>{label}</Text>
      <Text size="xs" fw={500}>{value}</Text>
    </Group>
  );
}
